import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const ServicesOffering = ({ }) => {
  const [activeService, setActiveService] = useState('live');
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const services = [
    {
      id: 'live',
      icon: 'Video',
      title: 'Live Webinars',
      subtitle: 'Real-Time Compliance Training',
      description: 'Interactive live sessions with compliance experts. Ask questions, participate in discussions, and earn CPE credits.',
      features: [
        'Real-time Q&A with experts',
        'CPE/SHRM/HRCI credits',
        'Interactive case studies',
        'Certificate of attendance',
        'Session recordings included'
      ],
      pricing: {
        from: '$139',
        popular: true
      },
      gradient: 'from-green-500 via-emerald-500 to-green-600',
      iconBg: 'bg-gradient-to-br from-green-100 to-emerald-100',
      iconColor: 'text-green-600',
      badge: 'Most Popular',
      badgeColor: 'bg-gradient-to-r from-green-500 to-emerald-600',
      route: '/webinars/live'
    },
    {
      id: 'recorded',
      icon: 'Play',
      title: 'On-Demand Library',
      subtitle: 'Learn at Your Pace',
      description: 'Access 500+ recorded compliance courses covering OSHA, HIPAA, HR regulations, and industry standards.',
      features: [
        'Unlimited 24/7 access',
        'Download certificates',
        'Searchable content library',
        'Mobile & desktop friendly',
        'Regular content updates'
      ],
      pricing: {
        from: '$189',
        popular: false
      },
      gradient: 'from-emerald-500 via-green-500 to-emerald-600',
      iconBg: 'bg-gradient-to-br from-emerald-100 to-green-100',
      iconColor: 'text-emerald-600',
      badge: 'Best Value',
      badgeColor: 'bg-gradient-to-r from-emerald-500 to-green-600',
      route: '/webinars/recorded'
    },
    {
      id: 'package',
      icon: 'Award',
      title: 'Enterprise Solutions',
      subtitle: 'Custom Training Programs',
      description: 'Tailored compliance training packages for teams with dedicated support, custom content, and reporting.',
      features: [
        'Custom training paths',
        'Team management dashboard',
        'Compliance tracking & reporting',
        'Dedicated account manager',
        'Volume discounts available'
      ],
      pricing: {
        from: '$699',
        popular: false
      },
      gradient: 'from-green-600 via-emerald-600 to-green-700',
      iconBg: 'bg-gradient-to-br from-green-100 to-emerald-100',
      iconColor: 'text-green-700',
      badge: 'For Teams',
      badgeColor: 'bg-gradient-to-r from-green-600 to-emerald-700',
      route: '/webinars/live'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95 
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        type: "spring",
        stiffness: 100
      }
    }
  };

  const handleServiceNavigation = (service) => {
    navigate(service.route);
  };

  const handleContactUs = () => {
    navigate('/contact');
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-green-50 via-emerald-50/30 to-green-50/30 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>
      <div className="absolute top-20 right-10 w-64 h-64 bg-green-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-64 h-64 bg-emerald-200/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full border border-green-200 text-sm mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <motion.div
              animate={{ rotate: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Icon name="Briefcase" size={16} className="text-green-700" />
            </motion.div>
            <span className="font-semibold text-green-800">Flexible Training Options</span>
          </motion.div>

          <motion.h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <motion.span 
              className="bg-gradient-to-r from-emerald-700 via-green-600 to-emerald-700 bg-clip-text text-transparent"
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{ 
                duration: 5, 
                repeat: Infinity,
                ease: "linear" 
              }}
              style={{ backgroundSize: '200% 100%' }}
            >
              Choose Your Compliance Path
            </motion.span>
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed"
          >
            ComplianceTrained offers flexible learning formats to meet your regulatory training needs. 
            From live expert sessions to on-demand courses and enterprise solutions.
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {services.map((service) => (
            <motion.div
              key={service.id}
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3, type: "spring", stiffness: 300 }
              }}
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
              className={`relative bg-gradient-to-b from-white to-emerald-50 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group cursor-pointer border-2 ${
                service.pricing.popular 
                  ? 'border-green-400 shadow-lg shadow-green-500/20' 
                  : 'border-green-100 hover:border-emerald-300'
              }`}
              onClick={() => {
                setActiveService(service.id);
                handleServiceNavigation(service);
              }}
            >
              {/* Top accent line */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>

              {/* Floating badge */}
              <div className="absolute top-4 right-4 z-10">
                <motion.span 
                  className={`${service.badgeColor} text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg`}
                  animate={service.pricing.popular ? { 
                    scale: [1, 1.1, 1],
                    boxShadow: [
                      "0 0 0 0 rgba(16, 185, 129, 0)",
                      "0 0 0 6px rgba(16, 185, 129, 0.3)",
                      "0 0 0 0 rgba(16, 185, 129, 0)"
                    ]
                  } : {}}
                  transition={service.pricing.popular ? { 
                    duration: 2,
                    repeat: Infinity 
                  } : {}}
                >
                  {service.badge}
                </motion.span>
              </div>

              {/* Header with Gradient */}
              <div className={`bg-gradient-to-br ${service.gradient} p-6 sm:p-8 text-white relative overflow-hidden`}>
                {/* Animated background elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12"></div>
                
                <div className="relative">
                  <motion.div 
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6, type: "spring" }}
                    className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 ${service.iconBg} rounded-xl mb-4 shadow-lg border border-white/30`}
                  >
                    <Icon name={service.icon} size={24} className={service.iconColor} />
                  </motion.div>
                  
                  <motion.h3 
                    className="text-xl sm:text-2xl font-bold mb-2"
                    animate={hoveredCard === service.id ? { x: 5 } : { x: 0 }}
                  >
                    {service.title}
                  </motion.h3>
                  <p className="text-sm opacity-90 mb-3">{service.subtitle}</p>
                  
                  {/* Pricing */}
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs opacity-75">Starting from</span>
                    <motion.span 
                      className="text-2xl font-black"
                      animate={hoveredCard === service.id ? { scale: 1.1 } : { scale: 1 }}
                    >
                      {service.pricing.from}
                    </motion.span>
                    <span className="text-xs opacity-75">/session</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8">
                <p className="text-gray-600 mb-6 leading-relaxed text-sm sm:text-base">
                  {service.description}
                </p>

                {/* Features List */}
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, index) => (
                    <motion.li 
                      key={index} 
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ x: 3 }}
                    >
                      <motion.div 
                        className="w-5 h-5 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border border-green-200"
                        whileHover={{ rotate: 90 }}
                      >
                        <Icon name="Check" size={12} className="text-green-600" />
                      </motion.div>
                      <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* Action Button */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative overflow-hidden rounded-xl group/btn"
                >
                  <motion.button
                    className={`w-full py-3 px-4 bg-gradient-to-r ${service.gradient} text-white font-bold rounded-xl transition-all duration-300 shadow-md hover:shadow-xl group-hover/btn:shadow-green-500/30`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleServiceNavigation(service);
                    }}
                  >
                    <span className="flex items-center justify-center gap-2 relative z-10">
                      Explore Now
                      <motion.div
                        animate={hoveredCard === service.id ? { x: 5 } : { x: 0 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <Icon name="ArrowRight" size={16} />
                      </motion.div>
                    </span>
                  </motion.button>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700"></div>
                </motion.div>
              </div>

              {/* Bottom decoration */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12 lg:mt-16"
        >
          <div className="bg-gradient-to-br from-white to-emerald-50 rounded-2xl shadow-xl p-6 sm:p-8 border-2 border-green-200 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-green-200/20 rounded-full blur-3xl"></div>
            
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 relative z-10">
              <div className="text-left flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div 
                    className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center border border-green-200 shadow-lg"
                    whileHover={{ rotate: 10 }}
                  >
                    <Icon name="HelpCircle" size={24} className="text-emerald-600" />
                  </motion.div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                      Need Expert Guidance?
                    </h3>
                    <motion.p 
                      className="text-sm text-gray-600 mt-1"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      Our compliance specialists can help you choose the right training solution.
                    </motion.p>
                  </div>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 text-white font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-xl shadow-lg hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-300 whitespace-nowrap group"
                onClick={handleContactUs}
              >
                <motion.div
                  animate={{ rotate: [0, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Icon name="MessageCircle" size={20} />
                </motion.div>
                Schedule Consultation
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Icon name="ArrowRight" size={18} />
                </motion.div>
              </motion.button>
            </div>
          </div>

          {/* Stats Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8"
          >
            {[
              { number: '500+', label: 'Compliance Courses' },
              { number: '10K+', label: 'Professionals Trained' },
              { number: '98%', label: 'Satisfaction Rate' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-green-100"
              >
                <div className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesOffering;